/**
 * Pure helpers for CreatableCombobox (plain vs labeled options).
 * For Media Devoted | Created By Ciro Autiero
 */

export interface LabeledComboboxOption {
  label: string;
  value: string;
}

export function safeStr(v: unknown): string {
  return String(v ?? '');
}

export function normalizePlainOptions(options: string[], sort = true): string[] {
  const list = Array.isArray(options) ? options : [];
  const unique = [...new Set(list.map((o) => safeStr(o).trim()).filter(Boolean))];
  return sort ? unique.sort() : unique;
}

export function filterPlainOptions(unique: string[], inputValue: unknown): string[] {
  const q = safeStr(inputValue).toLowerCase().trim();
  if (!q) return unique;
  return unique.filter((o) => safeStr(o).toLowerCase().includes(q));
}

/**
 * Builds labeled combobox options from API output.
 * Supports `{ label, value }[]`, legacy `string[]`, and loose keys (`Label` / `Value`).
 */
export function normalizeLabeledOptions(
  opts: unknown,
  sort = true,
): LabeledComboboxOption[] {
  const rows = Array.isArray(opts) ? opts : [];
  const seen = new Set<string>();
  const out: LabeledComboboxOption[] = [];
  for (const raw of rows) {
    if (typeof raw === 'string' || typeof raw === 'number') {
      const s = safeStr(raw).trim();
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push({ label: s, value: s });
      continue;
    }
    if (!raw || typeof raw !== 'object') continue;
    const o = raw as Record<string, unknown>;
    const label = safeStr(o.label ?? o.Label).trim();
    const value = safeStr(o.value ?? o.Value).trim();
    if (!label || !value || seen.has(value)) continue;
    seen.add(value);
    out.push({ label, value });
  }
  if (sort) out.sort((a, b) => a.label.localeCompare(b.label));
  return out;
}

export function filterLabeledOptions(
  unique: LabeledComboboxOption[],
  inputValue: unknown,
): LabeledComboboxOption[] {
  const q = safeStr(inputValue).toLowerCase().trim();
  if (!q) return unique;
  return unique.filter(
    (o) =>
      safeStr(o?.label).toLowerCase().includes(q) ||
      safeStr(o?.value).toLowerCase().includes(q),
  );
}

export function labeledExactMatch(
  unique: LabeledComboboxOption[],
  inputValue: unknown,
): boolean {
  const t = safeStr(inputValue).trim().toLowerCase();
  if (!t) return false;
  return unique.some(
    (o) =>
      safeStr(o?.label).trim().toLowerCase() === t ||
      safeStr(o?.value).trim().toLowerCase() === t,
  );
}

export function displayForCommittedValue(
  value: unknown,
  labeled: LabeledComboboxOption[] | undefined,
): string {
  const raw = safeStr(value);
  if (!labeled) return raw;
  const trimmedRaw = raw.trim();
  const hit = labeled.find(
    (o) =>
      safeStr(o?.value) === raw ||
      safeStr(o?.label).trim() === trimmedRaw ||
      safeStr(o?.value).trim() === trimmedRaw,
  );
  const label = hit?.label != null ? safeStr(hit.label).trim() : '';
  return label || raw;
}
